import React, { useEffect, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { formatDate } from '../../helpers/formatters';
import { useAuth, useSettings, useShopCurrency } from '../../hooks';
import { acceptWindow } from '../../helpers/acceptWindow';
import { GET_ORDER_QUERY, ACCEPT_ORDER_MUTATION, CANCEL_ORDER_MUTATION } from './queries';
import { SellerOrderLoader } from './SellerOrderLoader';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IAlertProps, Page, Spinner, useNotification } from '../../components';
import { Button } from '../../components/primitives';
import { Fulfillment, MutationAcceptSellerOrderArgs, MutationCancelSellerOrderArgs, SellerOrder as SellerOrderType, SellerOrderStatus } from '@shopify-multiseller/graphql/partner/types';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { SectionCard } from '../../components/SectionCard';
import { Timeline } from '../../components/Timeline';
import { buildOrderTimeline } from './orderTimeline';
import { payoutMethodState } from './payoutState';
import { OrderItems } from './OrderItems';
import { PayoutMaths } from './PayoutMaths';
import { TrackingCard } from './TrackingCard';

import { AdminUI } from '../../ui/AdminUI';
import { SellerOrderStatusBadge } from '../../components/StatusBadge/SellerOrderStatusBadge';
import EmptyPage from '../EmptyPage/EmptyPage';
import { AcceptSellerOrderModal } from './AcceptSellerOrderModal';
import { ICancelSellerOrderValues, CancelSellerOrderModal } from './CancelSellerOrderModal';
import { RejectSellerOrderModal } from './RejectSellerOrderModal';
import CustomerDetail from './CustomerDetail';
import PayoutSummary from './PayoutSummary';

export const SellerOrder: React.FC = () => {

    const params = useParams();
    const navigate = useNavigate();
    const notification = useNotification();
    const currencyCode = useShopCurrency();


    const [sellerOrder, setSellerOrder] = useState<SellerOrderType | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [isCancelSellerOrderModalVisible, setIsCancelSellerOrderModalVisible] = useState<boolean>(false);
    const [isRejectSellerOrderModalVisible, setIsRejectSellerOrderModalVisible] = useState<boolean>(false);
    const acceptWindowHours = useSettings().sellerOrderAccept?.orderAcceptTimeout ?? 0;
    // Whether money can reach this seller at all. Read here, where the seller
    // is, and handed to the timeline, which only knows about the order.
    const payoutMethod = payoutMethodState(useAuth()?.user?.seller);
    const [isAcceptSellerOrderModalVisible, setIsAcceptSellerOrderModalVisible] = useState<boolean>(false);
    const [pageError, setPageError] = useState<boolean>(false);
    const [errorAlert, setErrorAlert] = useState<IAlertProps | undefined>(undefined);


    const [getSellerOrder] = useLazyQuery<
        { sellerOrder: SellerOrderType },
        { id: string }
    >(GET_ORDER_QUERY);

    useEffect(() => {
        getSellerOrder({
            variables: {
                id: params.sellerOrderId!,
            }
        }).then((result: any) => {
            if (result.error) {
                setPageError(true);
                setLoading(false);

            } else {
                setSellerOrder(result.data?.sellerOrder);
                setLoading(false);
            }
        }).catch(() => {
            setPageError(true);
            setLoading(false);
        }
        );
    }, []);

    const [acceptSellerOrder] = useMutation<
        { acceptSellerOrder: SellerOrderType },
        MutationAcceptSellerOrderArgs
    >(ACCEPT_ORDER_MUTATION);

    const [cancelSellerOrder] = useMutation<
        { cancelSellerOrder: SellerOrderType },
        MutationCancelSellerOrderArgs
    >(CANCEL_ORDER_MUTATION);


    const handleCancelSellerOrderOnSubmit = (values: ICancelSellerOrderValues) => {
        setUpdating(true);
        setErrorAlert(undefined);
        sellerOrder && cancelSellerOrder({
            variables: {
                id: sellerOrder.id, details: {
                    reason: values.reason,
                    refund: values.refund,
                    email: values.email,
                }
            },
        }).then((res) => {
            setSellerOrder(res.data?.cancelSellerOrder);
            setIsCancelSellerOrderModalVisible(false);
            setUpdating(false);
            notification.success({
                message: `Order #${sellerOrder.idAlias} has been cancelled.`,
            });
        }).catch(() => {
            setUpdating(false);
            setPageError(true);
            setIsCancelSellerOrderModalVisible(false);
            setErrorAlert({
                status: "error",
                title: "Failed",
                message: `Order #${sellerOrder.idAlias} cancel has failed.`,
            });
            notification.error({
                message: `Order #${sellerOrder.idAlias} cancel has failed.`,
            });
        });
    }

    const handleOnAcceptSellerOrder = (value: boolean) => {
        setUpdating(true);
        setErrorAlert(undefined);
        setIsAcceptSellerOrderModalVisible(false);
        setIsRejectSellerOrderModalVisible(false);
        sellerOrder && acceptSellerOrder({
            variables: { id: sellerOrder.id, accepted: value },
        }).then((res) => {
            setSellerOrder(res.data?.acceptSellerOrder);
            setUpdating(false);

            value && notification.success({
                message: "Accepted",
                description: `Order #${sellerOrder.idAlias} has been accepted.`,
            });
            !value && notification.warn({
                message: "Rejected",
                description: `Order #${sellerOrder.idAlias} has been rejected.`,
            });
        }).catch(() => {
            setUpdating(false);
            setErrorAlert({
                status: "error",
                message: `Order #${sellerOrder.idAlias} accept has failed.`,
            });
            value && notification.error({
                message: "Failed",
                description: `Order #${sellerOrder.idAlias} accept has failed.`,
            });
            !value && notification.error({
                message: "Failed",
                description: `Order #${sellerOrder.idAlias} reject has failed.`,
            });
        });
    }

    if (loading) return <AdminUI><SellerOrderLoader /></AdminUI>;

    if (pageError) {
        return (
            <AdminUI>
                <EmptyPage
                    title="This order didn't load"
                    description="The connection dropped or the server is busy. The order itself is fine."
                    icon={ExclamationTriangleIcon}
                    action={{ label: 'Try again', onAction: () => window.location.reload() }}
                    secondary={<Link className="text-primary hover:underline" to="/orders">Back to orders</Link>}
                />
            </AdminUI>
        )
    }

    if (!sellerOrder) {
        return (
            <AdminUI>
                <EmptyPage
                    title="Order not found"
                    description="It may have been cancelled, or the link may be for a different seller account."
                    icon={ExclamationTriangleIcon}
                    action={{ label: 'Back to orders', onAction: () => navigate('/orders') }}
                />
            </AdminUI>
        )
    }

    /*
      Header actions, and nothing else. These were wrapped in `flex justify-end
      mt-6` — a right-aligning, top-margined block inside a slot that already
      right-aligns and sits on the heading's baseline, so the buttons hung six
      pixels below the title on every order. The pair also mixed default and
      `sm` sizes between the two states, so Accept/Reject came out visibly
      taller than Cancel/Fulfil on otherwise identical screens.
    */
    const renderPendingOrderActions = () => (
        <>
            {!updating && (
                <Button
                    data-testid="order-reject-button"
                    disabled={updating}
                    // A button, not a ghost that reads as a link, and no
                    // ellipsis: rejecting is one of the two answers this
                    // screen is asking for, so it should look like the other
                    // one. It is also what the triage queue already renders
                    // for the same decision.
                    variant='outline'
                    onClick={() => setIsRejectSellerOrderModalVisible(true)}
                >
                    Reject
                </Button>
            )}
            <Button
                data-testid="order-accept-button"
                variant='primary'
                disabled={updating}
                loading={updating}
                onClick={() => setIsAcceptSellerOrderModalVisible(true)}
            >
                Accept order
            </Button>
        </>
    );

    const renderPendingFulfillmentActions = () => (
        <>
            {!updating && (
                <Button
                    data-testid="order-cancel-button"
                    disabled={updating}
                    variant='ghost'
                    onClick={() => setIsCancelSellerOrderModalVisible(true)}
                >
                    Cancel
                </Button>
            )}
            <Button
                data-testid="order-fulfill-button"
                variant='primary'
                disabled={updating}
                loading={updating}
                onClick={() => navigate(`/orders/${sellerOrder.id}/fulfill`)}
            >
                Fulfill
            </Button>
        </>
    );

    const renderActionButtons = (status: SellerOrderStatus) => {
        switch (status) {
            case SellerOrderStatus.Pending:
                return renderPendingOrderActions();
            case SellerOrderStatus.PendingFulfillment:
                return renderPendingFulfillmentActions();
            default:
                return null;
        }
    }

    const alerts = (): IAlertProps[] => {
        const alerts: IAlertProps[] = [];
        if (errorAlert) alerts.push(errorAlert);
        // An unaccepted order has a clock on it, and nothing on this screen
        // said so — a seller could lose the sale to a timeout without ever
        // being told one was running. The window is the shop's
        // orderAcceptTimeout setting, already fetched with the seller, and the
        // deadline is that many hours from when the order was placed.
        if (sellerOrder?.status === SellerOrderStatus.Pending && acceptWindowHours > 0) {
            const left = acceptWindow(sellerOrder.orderDate, acceptWindowHours);
            const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;
            alerts.push({
                status: 'warning',
                // Two runs, as drawn: the deadline, then why it matters. Said
                // as one sentence it read as a single block of small print.
                message: (
                    <>
                        <span className="font-semibold">
                            {left?.expired
                                ? 'This order has expired'
                                : `Respond within ${left?.duration}`}
                        </span>{' '}
                        <span>{`Orders not accepted within ${plural(acceptWindowHours, 'hour')} expire and are refunded to the customer.`}</span>
                    </>
                ),
            });
        }
        // Sentence case, and each one says what actually happened rather than
        // restating its own Title Case heading back at the reader ("Order
        // Rejected" / "This order has been rejected.").
        if (sellerOrder?.status === SellerOrderStatus.Rejected) {
            alerts.push({
                status: "warning",
                message: "You rejected this order, so it has gone back to the shop.",
            });
        }
        if (sellerOrder?.status === SellerOrderStatus.AcceptTimeout) {
            alerts.push({
                status: "warning",
                message: "This order wasn't accepted in time, so it has gone back to the shop.",
            });
        }
        if (sellerOrder?.status === SellerOrderStatus.FulfillmentTimeout) {
            alerts.push({
                status: "warning",
                message: "This order wasn't fulfilled in time, so it has gone back to the shop.",
            });
        }
        if (sellerOrder?.status === SellerOrderStatus.RejectedThirdParty) {
            alerts.push({
                status: "warning",
                message: "A third party rejected this order, so it has gone back to the shop.",
            });
        }
        return alerts;

    };


    const isAwaitingAcceptance = sellerOrder.status === SellerOrderStatus.Pending;

    const renderTitleMeta = () => {
        if (updating) {
            return <Spinner size="small" />
        } else
            return <SellerOrderStatusBadge size='medium' status={sellerOrder.status} />;
    }

    return (
        <AdminUI>
            <>
                <Page
                    title={`Order #${sellerOrder.idAlias}`}
                    actions={renderActionButtons(sellerOrder.status)}
                    breadcrumbs={[
                        { content: 'Orders', url: '/orders' },
                        { content: sellerOrder.idAlias, url: `/orders/${sellerOrder.id}`, current: true },
                    ]}
                    // The shop's own order number too — a seller chasing this
                    // order with the shop is asked for that number, not ours.
                    subtitle={[
                        `Placed ${formatDate(sellerOrder.orderDate, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                        })}`,
                        sellerOrder.shopifyOrderId && `Shopify order #${sellerOrder.shopifyOrderId}`,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                    titleMeta={renderTitleMeta()}
                    alerts={
                        alerts()
                    }
                >
                    {/* Before acceptance the money card is a three-line quote,
                        so it sits in the narrow column beside the customer it
                        concerns rather than under the items. Once the order is
                        live it is a full breakdown and takes the wide one. */}
                    <div className="flex flex-wrap items-start justify-center gap-5">
                        <div className="min-w-[51%] flex-[2_2_30rem] space-y-5">
                            <OrderItems
                                items={sellerOrder.items}
                                currencyCode={currencyCode}
                                showTotal={isAwaitingAcceptance}
                            />
                            {!isAwaitingAcceptance && (
                                <PayoutMaths order={sellerOrder} currencyCode={currencyCode} />
                            )}
                            {sellerOrder.fulfillment && (
                                <TrackingCard
                                    orderId={sellerOrder.id}
                                    fulfillment={sellerOrder.fulfillment}
                                    onUpdated={(fulfillment: Fulfillment) =>
                                        setSellerOrder({ ...sellerOrder, fulfillment })
                                    }
                                />
                            )}
                        </div>
                        <div className="min-w-0 flex-[1_1_15rem] space-y-5">
                            {isAwaitingAcceptance && (
                                <PayoutMaths order={sellerOrder} currencyCode={currencyCode} />
                            )}
                            <PayoutSummary order={sellerOrder} />
                            <CustomerDetail order={sellerOrder} />
                            {/* Under the customer, down the side, and read
                                downwards. It ran across the top of the page
                                before, where four steps spread over the full
                                width read as a banner rather than as one
                                order's history. */}
                            <SectionCard title="Timeline" testId="order-timeline-section">
                                <Timeline entries={buildOrderTimeline(sellerOrder, payoutMethod)} />
                            </SectionCard>
                        </div>
                    </div>
                </Page>
                <CancelSellerOrderModal
                    loading={updating}
                    open={isCancelSellerOrderModalVisible}
                    onSubmit={handleCancelSellerOrderOnSubmit} onClose={() => setIsCancelSellerOrderModalVisible(false)} />
                <RejectSellerOrderModal
                    open={isRejectSellerOrderModalVisible}
                    onClose={() => setIsRejectSellerOrderModalVisible(false)}
                    onReject={() => handleOnAcceptSellerOrder(false)}
                    updating={updating}
                />
                <AcceptSellerOrderModal
                    open={isAcceptSellerOrderModalVisible}
                    onClose={() => setIsAcceptSellerOrderModalVisible(false)}
                    onAccept={() => handleOnAcceptSellerOrder(true)}
                    updating={updating}
                /></>
        </AdminUI>
    );
};
